import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ChefHat,
  Sparkles,
  Salad,
  ShieldCheck,
  Leaf,
  Users,
  Clock,
  UtensilsCrossed,
  Package,
  FileText,
} from "lucide-react";
import { useSeo } from "@/components/Seo";

const HERO_IMAGE = "/api/images/13d8ecd1-bfe7-4b4b-b819-814cadbc3244";

const FUNCTIONS = [
  {
    title: "Wedding receptions",
    text: "Plated courses or buffet-style spreads for reception evenings — welcome drinks, starters, mains, live counters and desserts arranged around your guest count.",
  },
  {
    title: "Marriage ceremonies",
    text: "Traditional vegetarian meals for the wedding day itself, planned around the ceremony timings so food is served fresh and on time.",
  },
  {
    title: "Engagement functions",
    text: "Lighter menus that work well for engagements — chaat and pasta live counters, mocktails and a curated selection of starters and sweets.",
  },
  {
    title: "Pre-wedding functions",
    text: "Haldi, mehndi, sangeet and other pre-wedding gatherings at home or a venue, with menus sized for smaller, informal groups.",
  },
  {
    title: "Family wedding celebrations",
    text: "Intimate family lunches and dinners around the wedding week, with the same menu flexibility as the main functions.",
  },
];

const WHY = [
  { icon: Salad, title: "Vegetarian focus", text: "A pure-vegetarian kitchen with a menu built around vegetarian starters, mains, live counters and sweets." },
  { icon: UtensilsCrossed, title: "130+ dishes to choose from", text: "Build each function's menu from a large, real menu — not a fixed thali." },
  { icon: ShieldCheck, title: "Hygienic & safe", text: "Food prepared in a hygienic kitchen with fresh ingredients." },
  { icon: Users, title: "Experienced team", text: "A team that has served weddings, receptions and family celebrations across Mumbai." },
  { icon: Clock, title: "On-time service", text: "Catering planned around your function timings." },
  { icon: Leaf, title: "Transparent quotations", text: "You get a written, itemised quotation to review and approve before anything is confirmed." },
];

export default function WeddingCateringMumbai() {
  useSeo({
    title: "Wedding Catering in Mumbai | Vegetarian Caterers | Tezz Khana",
    description:
      "Vegetarian wedding catering in Mumbai by Tezz Khana. Custom menus for weddings, receptions and family celebrations across Mumbai, Navi Mumbai and Thane. Request a quote.",
    path: "/wedding-catering-mumbai",
    ogTitle: "Wedding Catering in Mumbai | Tezz Khana",
    ogDescription:
      "Vegetarian wedding catering in Mumbai with flexible menus and customized catering options from Tezz Khana.",
  });

  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "ld-wedding-catering";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      serviceType: "Wedding catering",
      name: "Wedding Catering in Mumbai",
      description:
        "Vegetarian wedding catering in Mumbai for weddings, receptions, engagements and family celebrations, with flexible menus and custom quotations.",
      url: "https://tezzkhana.com/wedding-catering-mumbai",
      areaServed: [
        { "@type": "City", name: "Mumbai" },
        { "@type": "City", name: "Navi Mumbai" },
        { "@type": "City", name: "Thane" },
      ],
      provider: {
        "@type": "FoodEstablishment",
        name: "Tezz Khana Catering Service",
        url: "https://tezzkhana.com",
        servesCuisine: "Indian vegetarian",
        address: {
          "@type": "PostalAddress",
          addressLocality: "Goregaon East, Mumbai",
          addressRegion: "Maharashtra",
          addressCountry: "IN",
        },
      },
    });
    document.head.appendChild(script);
    return () => {
      document.getElementById("ld-wedding-catering")?.remove();
    };
  }, []);

  return (
    <>
      {/* HERO */}
      <section className="container-x py-14 sm:py-20" data-testid="wcm-hero">
        <div className="grid items-center gap-10 lg:grid-cols-[1.1fr_1fr]">
          <div>
            <span className="kbd-eyebrow">
              <Sparkles className="h-3 w-3" /> Tezz Khana Catering Service
            </span>
            <h1 className="mt-4 font-serif-display text-4xl font-semibold leading-tight text-secondary sm:text-5xl lg:text-6xl">
              Wedding Catering in Mumbai
            </h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-muted-foreground">
              Make your wedding celebration memorable with thoughtfully prepared food and
              flexible catering options from Tezz Khana. Choose dishes and packages that
              suit your event, guest count and preferences.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Link
                to="/request-quote"
                data-testid="wcm-hero-quote"
                className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-primary px-6 text-sm font-semibold text-primary-foreground transition-transform hover:-translate-y-0.5"
              >
                Request a Custom Quote <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/build-your-menu"
                data-testid="wcm-hero-build"
                className="inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-border px-6 text-sm font-semibold text-secondary hover:bg-muted"
              >
                <ChefHat className="h-4 w-4" /> Build Your Menu
              </Link>
            </div>
          </div>
          <img
            src={HERO_IMAGE}
            alt="Paneer tikka starter from the Tezz Khana vegetarian wedding menu"
            className="h-64 w-full rounded-3xl object-cover shadow-lg sm:h-80 lg:h-96"
            loading="eager"
          />
        </div>
      </section>

      {/* INTRODUCTION */}
      <section className="container-x pb-14" data-testid="wcm-intro">
        <div className="max-w-3xl space-y-4 text-sm leading-relaxed text-muted-foreground sm:text-base">
          <p>
            Tezz Khana is a vegetarian catering service based in Goregaon East, providing
            wedding catering across Mumbai. Whether you are planning a large reception or
            an intimate family function, the catering is planned around the type of
            wedding function, your guest requirements and the menu you prefer.
          </p>
          <p>
            There is no fixed, one-size-fits-all wedding menu — you can browse the{" "}
            <Link to="/menu" className="font-semibold text-primary hover:underline">
              full menu
            </Link>
            , pick from ready-made{" "}
            <Link to="/packages" className="font-semibold text-primary hover:underline">
              packages
            </Link>{" "}
            or build your own selection, and then request a customised quotation for your
            function.
          </p>
        </div>
      </section>

      {/* WEDDING FUNCTIONS */}
      <section className="container-x pb-16" data-testid="wcm-functions">
        <span className="kbd-eyebrow">Wedding catering options</span>
        <h2 className="mt-3 text-base font-semibold text-secondary md:text-lg">
          Catering for every wedding function
        </h2>
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {FUNCTIONS.map((f) => (
            <div key={f.title} className="rounded-2xl border border-border/70 bg-card p-6">
              <h3 className="text-sm font-semibold text-secondary">{f.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* MENU & FOOD OPTIONS */}
      <section className="container-x pb-16" data-testid="wcm-menu">
        <div className="rounded-3xl border border-border/70 bg-card p-8 sm:p-10">
          <span className="kbd-eyebrow">Menu & food options</span>
          <h2 className="mt-3 text-base font-semibold text-secondary md:text-lg">
            Choose from 130+ vegetarian dishes
          </h2>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-muted-foreground">
            The Tezz Khana menu covers everything a wedding spread needs: welcome drinks,
            mocktails and fresh juices, vegetarian starters, soups and salads, veg and
            paneer main courses, live counters for pasta and chaat, and a wide range of
            desserts and traditional sweets. Mix and match across categories to shape a
            menu that fits your functions and your guests.
          </p>
          <Link
            to="/menu"
            data-testid="wcm-menu-link"
            className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline"
          >
            Explore the full menu <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* PACKAGES + BUILD YOUR MENU */}
      <section className="container-x grid gap-6 pb-16 lg:grid-cols-2" data-testid="wcm-packages">
        <div className="rounded-3xl border border-border/70 bg-card p-8">
          <span className="grid h-11 w-11 place-items-center rounded-xl bg-primary/10 text-primary">
            <Package className="h-5 w-5" />
          </span>
          <h2 className="mt-4 text-base font-semibold text-secondary md:text-lg">Flexible packages</h2>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
            If you'd rather start from a ready structure, Tezz Khana offers Silver, Gold
            and Platinum packages, plus a fully custom option. Each package can be
            adjusted to your functions and guest count before you receive a quotation.
          </p>
          <Link
            to="/packages"
            data-testid="wcm-packages-link"
            className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline"
          >
            View packages <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="rounded-3xl border border-border/70 bg-card p-8">
          <span className="grid h-11 w-11 place-items-center rounded-xl bg-primary/10 text-primary">
            <ChefHat className="h-5 w-5" />
          </span>
          <h2 className="mt-4 text-base font-semibold text-secondary md:text-lg">Build your wedding menu</h2>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
            Prefer to hand-pick every dish? Use the menu builder to select starters,
            mains, live counters and sweets dish by dish, exactly as you want them served
            at your wedding.
          </p>
          <Link
            to="/build-your-menu"
            data-testid="wcm-build-link"
            className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline"
          >
            Start building your menu <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section className="container-x pb-16" data-testid="wcm-why">
        <span className="kbd-eyebrow">Why choose Tezz Khana</span>
        <h2 className="mt-3 text-base font-semibold text-secondary md:text-lg">
          Wedding caterers Mumbai families can plan with confidently
        </h2>
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {WHY.map((w) => (
            <div key={w.title} className="rounded-2xl border border-border/70 bg-card p-6">
              <w.icon className="h-5 w-5 text-primary" />
              <h3 className="mt-3 text-sm font-semibold text-secondary">{w.title}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{w.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* SERVICE AREA */}
      <section className="container-x pb-16" data-testid="wcm-service-area">
        <div className="rounded-3xl border border-border/70 bg-card p-8 sm:p-10">
          <span className="kbd-eyebrow">Service area</span>
          <h2 className="mt-3 text-base font-semibold text-secondary md:text-lg">
            Serving weddings across Mumbai, Navi Mumbai and Thane
          </h2>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-muted-foreground">
            Based in Goregaon East, Tezz Khana handles marriage catering across Mumbai —
            from South Mumbai to the Western and Central suburbs — as well as Navi Mumbai
            and Thane. Wherever your venue is, share the location while requesting your
            quote and we'll plan the service around it.
          </p>
        </div>
      </section>

      {/* HOW TO REQUEST A QUOTE */}
      <section className="container-x pb-16" data-testid="wcm-steps">
        <span className="kbd-eyebrow">How it works</span>
        <h2 className="mt-3 text-base font-semibold text-secondary md:text-lg">
          How to request a wedding catering quote
        </h2>
        <div className="mt-6 grid gap-4 sm:grid-cols-3">
          {[
            { n: "1", t: "Choose your requirements", d: "Tell us the function type, date and expected guest count." },
            { n: "2", t: "Select or customise your menu", d: "Pick a package or build your own dish-by-dish selection." },
            { n: "3", t: "Request a quotation", d: "Receive an itemised quote to review, adjust and approve." },
          ].map((s) => (
            <div key={s.n} className="rounded-2xl border border-border/70 bg-card p-6">
              <span className="grid h-9 w-9 place-items-center rounded-full bg-primary/10 text-sm font-bold text-primary">{s.n}</span>
              <h3 className="mt-3 text-sm font-semibold text-secondary">{s.t}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{s.d}</p>
            </div>
          ))}
        </div>
        <Link
          to="/request-quote"
          data-testid="wcm-steps-quote-link"
          className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline"
        >
          <FileText className="h-4 w-4" /> Go to the quote request form <ArrowRight className="h-4 w-4" />
        </Link>
      </section>

      {/* FINAL CTA */}
      <section className="bg-primary py-14 text-primary-foreground sm:py-16" data-testid="wcm-cta">
        <div className="container-x flex flex-col items-center justify-between gap-6 sm:flex-row">
          <div>
            <h2 className="font-serif-display text-2xl font-semibold sm:text-3xl">
              Planning a Wedding in Mumbai?
            </h2>
            <p className="mt-2 max-w-xl text-sm text-primary-foreground/80">
              Tell us about your wedding event and catering requirements, and we&apos;ll
              help you plan a suitable menu.
            </p>
          </div>
          <div className="flex flex-col items-center gap-3 sm:flex-row">
            <Link
              to="/request-quote"
              data-testid="wcm-cta-quote"
              className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-accent px-6 text-sm font-semibold text-secondary transition-transform hover:-translate-y-0.5"
            >
              Request Custom Quote <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/build-your-menu"
              data-testid="wcm-cta-build"
              className="inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-primary-foreground/40 px-6 text-sm font-semibold text-primary-foreground hover:bg-primary-foreground/10"
            >
              <ChefHat className="h-4 w-4" /> Build Your Menu
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
